import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare } from "lucide-react";
import type { HubUserProfile } from "@hub/shared";
import { Avatar, Button, EmptyState, Field, Modal, Pill, SectionTitle, Spinner, Textarea, toast } from "@/ui";
import { api, useSession } from "../_shims";
import { useConnections, useMatchCards } from "../lib/hooks";
import { PageFrame } from "../components/PageFrame";
import { MatchRow } from "../components/MatchRow";
import { MatchModal } from "../components/MatchModal";
import { useMatchOpener } from "../components/useMatchOpener";
import { ConnectButton } from "./NetworkPage";
import { BlockReport } from "../components/BlockReport";
import s from "../network.module.css";

/**
 * There is no single-profile route in the API, so the profile is assembled from the directory and the connection list
 * (see the contract gaps in the report).
 */
function useProfile(userId: string | undefined) {
  const conns = useConnections();
  const dir = useQuery({ queryKey: ["network", "directory", "all"], queryFn: () => api.directory({ limit: 200 }), enabled: Boolean(userId) });
  const fromConn = conns.data?.connections.find((c) => c.otherUser.userId === userId);
  const profile: HubUserProfile | null = dir.data?.users.find((u) => u.userId === userId) ?? fromConn?.otherUser ?? null;
  return { profile, connection: fromConn ?? null, isLoading: dir.isLoading || conns.isLoading };
}

export function ProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useSession();
  const { profile, connection, isLoading } = useProfile(userId);
  const matches = useMatchCards();
  const opener = useMatchOpener();
  const [draft, setDraft] = useState<string | null>(null);
  const message = useMutation({
    mutationFn: (text: string) => api.startConversation({ toUserId: userId!, message: text }),
    onSuccess: (res) => {
      setDraft(null);
      navigate(`/messages/${res.conversation.id}`);
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send.", "danger"),
  });

  const related = (matches.data?.cards ?? []).filter((c) => c.needer.userId === userId || c.helper.userId === userId || c.needer.ownerUserId === userId || c.helper.ownerUserId === userId);

  return (
    <PageFrame
      title={profile?.displayName ?? "Profile"}
      subtitle={profile?.headline ?? undefined}
      actions={
        <Button variant="ghost" icon={<ArrowLeft size={14} />} onClick={() => navigate("/network")}>
          Network
        </Button>
      }
    >
      {!isSignedIn ? (
        <EmptyState title="Sign in to view Hub profiles" />
      ) : isLoading ? (
        <Spinner />
      ) : !profile ? (
        <EmptyState title="Profile not found">This person may have left Hub or is outside your reach.</EmptyState>
      ) : (
        <div className={s.split}>
          <div className="u-col" style={{ gap: 16 }}>
            <div className={s.profileCard}>
              <div className={s.profileHead}>
                <Avatar name={profile.displayName} src={profile.avatarUrl} size={56} seed={profile.userId} />
                <div className="u-grow">
                  <div style={{ fontSize: 16, fontWeight: 650 }}>{profile.displayName}</div>
                  {profile.headline && <div className={s.profileLine}>{profile.headline}</div>}
                  {profile.location && <div className={s.profileLine}>{profile.location}</div>}
                  {profile.mutualConnections > 0 && <div className={s.profileLine}>{profile.mutualConnections} mutual connection{profile.mutualConnections === 1 ? "" : "s"}</div>}
                </div>
                <div className="u-row">
                  <ConnectButton profile={profile} size="md" />
                  {profile.connectionStatus === "accepted" && (
                    <Button icon={<MessageSquare size={14} />} onClick={() => setDraft("")}>
                      Message
                    </Button>
                  )}
                  {profile.connectionStatus !== "self" && <BlockReport userId={profile.userId} name={profile.displayName} onDone={() => navigate("/network")} />}
                </div>
              </div>
              {connection && (
                <div className={`${s.small} ${s.muted}`}>
                  Connected since {new Date(connection.updated).toLocaleDateString()}. {connection.fromUserSharesNetwork && connection.toUserSharesNetwork ? "You both share your networks." : "Network sharing is off on at least one side."}
                </div>
              )}
            </div>
            <section>
              <SectionTitle>Offers</SectionTitle>
              {profile.services.length === 0 ? <div className={`${s.small} ${s.muted}`}>No offers listed.</div> : (
                <div className={s.chips}>
                  {profile.services.map((sv) => (
                    <Pill key={sv.id} tone="info" title={sv.category}>
                      {sv.title}
                    </Pill>
                  ))}
                </div>
              )}
            </section>
            <section>
              <SectionTitle>Needs</SectionTitle>
              {profile.needs.length === 0 ? <div className={`${s.small} ${s.muted}`}>No needs shared.</div> : (
                <div className={s.chips}>
                  {profile.needs.map((n) => (
                    <Pill key={n.id} tone="warning" title={n.category}>
                      {n.title}
                    </Pill>
                  ))}
                </div>
              )}
            </section>
            {related.length > 0 && (
              <section>
                <SectionTitle>Introductions with {profile.displayName}</SectionTitle>
                <div className={s.stack}>
                  {related.map((c) => (
                    <MatchRow key={c.matchId} card={c} onOpen={opener.open} onAction={opener.act} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={`Message ${profile?.displayName ?? ""}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!draft?.trim()} loading={message.isPending} onClick={() => draft && message.mutate(draft.trim())}>
              Send
            </Button>
          </>
        }
      >
        <Field label="Message" hint="Only the two of you can read this conversation.">
          <Textarea value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} rows={4} autoFocus />
        </Field>
      </Modal>
      <MatchModal card={opener.modal?.card ?? null} initialAction={opener.modal?.action ?? null} onClose={opener.close} />
    </PageFrame>
  );
}
